// src/dataProvider.js - Fixed tenant extraction
import { fetchUtils, HttpError } from 'react-admin';
import { extractTenantFromHostname, getTenantApiUrl } from './utils/tenant';

// Function to fetch call history from the call API
const fetchCallHistory = async (params) => {
  const { page = 1, perPage = 25 } = params.pagination || {};
  const teamId = localStorage.getItem('userTeamId');
  const token = localStorage.getItem('token');
  
  if (!teamId) {
    throw new Error('Team ID not available');
  }
  
  if (!token) {
    throw new Error('Authentication token not available');
  }
  
  try {
    // First get the voipnumber setting using the existing httpClient
    const settingsUrl = `teams/${teamId}/settings`;
    const { json: settings } = await httpClient(settingsUrl);
    
    const voipSetting = settings.find(setting => setting.key === 'voipnumber');
    if (!voipSetting) {
      throw new Error('VOIP number setting not found');
    }
    
    const voipNumber = voipSetting.value;
    const tenant = extractTenantFromHostname();
    
    // Calculate offset for pagination
    const offset = (page - 1) * perPage;
    
    // Determine the API URL based on environment (read from runtime config)
    const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

    let apiUrl;
    if (nodeEnv === 'development') {
      apiUrl = `http://${tenant}.api.wygc.eu:8080/api/call/matched?team_id=${teamId}&calleeId=${voipNumber}&limit=${perPage}&offset=${offset}`;
    } else {
      apiUrl = `https://${tenant}.api.wygc.eu/api/call/matched?team_id=${teamId}&calleeId=${voipNumber}&limit=${perPage}&offset=${offset}`;
    }
    
    console.log(`Fetching call history from: ${apiUrl}`);
    
    // Use fetch directly since this is an external API
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const calls = await response.json();
    
    // Transform the data to match React Admin expectations
    const transformedCalls = calls.map((call, index) => ({
      id: `${call.inboundCall.callId}_${index}_${page}`, // Create unique ID with page
      callId: call.inboundCall.callId,
      startedOn: call.inboundCall.startedOn,
      callerId: call.callerId,
      fullname: call.outboundCall?.fullname || '-',
      forwardedTo: call.forwardResult.forwarded
        ? (call.outboundCall?.calleeId || (call.forwardResult.reason === 'no_schedule' ? 'No schedule' : 'N/A'))
        : 'Not forwarded',
      callAnswered: call.forwardResult.answered,
      duration: call.forwardResult.forwarded
        ? call.outboundCall?.durationInSeconds || 0
        : call.inboundCall?.durationInSeconds || 0,
      inboundCall: call.inboundCall,
      outboundCall: call.outboundCall
    }));
    
    // For total count, we'll make a rough estimate since the API doesn't provide it
    // We'll assume there might be more data if we got exactly perPage items
    const estimatedTotal = calls.length === perPage ? (page * perPage) + perPage : (page - 1) * perPage + calls.length;
    
    return {
      data: transformedCalls,
      total: estimatedTotal
    };
  } catch (error) {
    console.error('Error fetching call history:', error);
    throw error;
  }
};

// Create the HTTP client with dynamic API URL
const httpClient = (url, options = {}) => {
  // Extract the path part from the provided URL
  let path = url;
  try {
    // Handle both absolute and relative URLs
    const urlObj = new URL(url, window.location.origin);
    // Keep only pathname and search parts
    path = urlObj.pathname + urlObj.search;
  } catch (e) {
    // If URL parsing fails, assume it's already a relative path
    console.warn('Error parsing URL:', e);
  }
  
  // Get the base API URL for the current environment
  const apiUrl = getTenantApiUrl();
  
  // Construct the full URL, ensuring no double slashes between apiUrl and path
  const fullUrl = apiUrl.endsWith('/') && path.startsWith('/') 
    ? `${apiUrl}${path.substring(1)}` 
    : `${apiUrl}${path}`;
  
  // Configure headers
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  
  // Add authentication token if available
  const token = localStorage.getItem('token');
  if (token) {
    options.headers.set('Authorization', `Bearer ${token}`);
  }
  
  // Log the URL being accessed (for debugging)
  console.log(`API Request to: ${fullUrl}`);
  
  // Store tenant from hostname in localStorage for reference
  const tenant = extractTenantFromHostname();
  localStorage.setItem('tenant', tenant);
  
  return fetchUtils.fetchJson(fullUrl, options);
};

// Base data provider implementation
const baseDataProvider = {
  getList: (resource, params) => {
    // Handle different resources with their respective endpoints
    let url = '';
    const teamId = localStorage.getItem('userTeamId');
    
    // Extract pagination, sorting, and filter parameters
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };
    const filters = params.filter || {};
    
    // Build query string for sorting and filtering
    const queryParams = {
      sort: field,
      order: order.toLowerCase(),
      ...filters
    };
    
    // Convert query params to string
    const queryString = Object.keys(queryParams)
      .filter(key => queryParams[key] !== undefined && queryParams[key] !== null)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(queryParams[key])}`)
      .join('&');
    
    switch (resource) {
      case 'contacts':
      case 'calendars':
      case 'settings':
        if (!teamId) {
          return Promise.reject('Team ID not available');
        }
        url = `teams/${teamId}/${resource}`;
        break;
      case 'users':
        // If we're listing users, we need to use the team's users endpoint
        if (teamId) {
          url = `teams/${teamId}/users`;
        } else {
          url = `users`;
        }
        break;
      case 'teams':
        // Teams are fetched directly
        url = `teams`;
        break;
      case 'callHistory':
        // Call history requires special handling - we'll fetch it from the call API
        return fetchCallHistory(params);
      case 'systemLogs':
        // System logs requires special handling with pagination
        if (!teamId) {
          return Promise.reject('Team ID not available');
        }
        // Add pagination parameters to URL
        const { page = 1, perPage = 25 } = params.pagination || {};
        const offset = (page - 1) * perPage;
        const systemLogsQueryParams = {
          limit: perPage,
          offset: offset,
          ...queryParams
        };
        const systemLogsQueryString = Object.keys(systemLogsQueryParams)
          .filter(key => systemLogsQueryParams[key] !== undefined && systemLogsQueryParams[key] !== null)
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(systemLogsQueryParams[key])}`)
          .join('&');
        url = `teams/${teamId}/logs/usage?${systemLogsQueryString}`;
        return httpClient(url).then(({ json }) => {
          return {
            data: json.map(item => ({ ...item, id: item.id || `${item.timestamp}_${item.team_id}` })),
            total: json.length < perPage ? (page - 1) * perPage + json.length : page * perPage + 1,
          };
        });
      case 'combinedLogs':
        // Combined logs requires special handling with pagination
        if (!teamId) {
          return Promise.reject('Team ID not available');
        }
        // Add pagination parameters to URL
        const combinedPage = params.pagination?.page || 1;
        const combinedPerPage = params.pagination?.perPage || 25;
        const combinedOffset = (combinedPage - 1) * combinedPerPage;
        const combinedLogsQueryParams = {
          limit: combinedPerPage,
          offset: combinedOffset,
          ...queryParams
        };
        const combinedLogsQueryString = Object.keys(combinedLogsQueryParams)
          .filter(key => combinedLogsQueryParams[key] !== undefined && combinedLogsQueryParams[key] !== null)
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(combinedLogsQueryParams[key])}`)
          .join('&');
        url = `teams/${teamId}/logs/combined?${combinedLogsQueryString}`;
        return httpClient(url).then(({ json }) => {
          return {
            data: json.map(item => ({ ...item, id: item.id || `${item.timestamp}_${item.team_id}` })),
            total: json.length < combinedPerPage ? (combinedPage - 1) * combinedPerPage + json.length : combinedPage * combinedPerPage + 1,
          };
        });
      default:
        url = `${resource}`;
    }
    
    // Append query string if it exists
    if (queryString) {
      url = `${url}?${queryString}`;
    }

    return httpClient(url).then(({ json }) => {
      // Client-side sorting if the API doesn't support it
      let sortedData = [...json];
      if (field && order) {
        sortedData.sort((a, b) => {
          const aValue = a[field] ? String(a[field]).toLowerCase() : '';
          const bValue = b[field] ? String(b[field]).toLowerCase() : '';
          
          if (order.toLowerCase() === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
          } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
          }
        });
      }
      
      return {
        data: sortedData.map(item => ({ ...item, id: item.id })),
        total: sortedData.length,
      };
    });
  },
  
  getOne: (resource, params) => {
    const teamId = localStorage.getItem('userTeamId');
    let url = '';
    
    switch (resource) {
      case 'contacts':
      case 'calendars':
      case 'settings':
        url = `teams/${teamId}/${resource}/${params.id}`;
        break;
      case 'users':
        url = `users/${params.id}`;
        break;
      case 'teams':
        url = `teams/${params.id}`;
        break;
      default:
        url = `${resource}/${params.id}`;
    }
    
    return httpClient(url).then(({ json }) => ({
      data: { ...json, id: json.id || params.id },
    }))
    .catch(() => {
      // For team resources without direct getOne endpoint, fallback to finding in list
      if (['contacts', 'calendars', 'settings'].includes(resource)) {
        const listUrl = `teams/${teamId}/${resource}`;
        return httpClient(listUrl).then(({ json }) => {
          const item = json.find(item => item.id === params.id);
          return { data: item || { id: params.id } };
        });
      }
      throw new Error(`Could not fetch ${resource} with id ${params.id}`);
    });
  },
  
  create: (resource, params) => {
    const teamId = localStorage.getItem('userTeamId');
    let url = '';
    
    switch (resource) {
      case 'contacts':
      case 'calendars':
      case 'settings':
        url = `teams/${teamId}/${resource}`;
        break;
      case 'users':
        url = `users`;
        break;
      case 'teams':
        url = `teams`;
        break;
      default:
        url = `${resource}`;
    }
    
    return httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id || params.data.id },
    })).catch(error => {
      // Extract error message from the API response
      const message = error.body?.message || error.body?.error || error.message || 'An error occurred while creating the resource';
      
      // Check if it's a validation error for username field
      if (message.toLowerCase().includes('username') || 
          message.toLowerCase().includes('email') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('duplicate')) {
        // Create HttpError with validation errors for specific fields
        throw new HttpError(message, error.status || 422, {
          errors: {
            username: [message]
          }
        });
      }
      
      // For other errors, throw standard HttpError
      throw new HttpError(message, error.status || 500);
    });
  },
  
  update: (resource, params) => {
    const teamId = localStorage.getItem('userTeamId');
    let url = '';
    
    // Create the URL based on the resource type
    switch (resource) {
      case 'contacts':
      case 'calendars':
      case 'settings':
        url = `teams/${teamId}/${resource}/${params.id}`;
        break;
      case 'users':
        url = `users/${params.id}`;
        break;
      case 'teams':
        url = `teams/${params.id}`;
        break;
      default:
        url = `${resource}/${params.id}`;
    }
    
    // For debugging
    console.log(`Making ${resource} update request to: ${url}`);
    console.log('Update payload:', params.data);
    
    // For all resources, simply send the data as-is from the form
    return httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: params.id },
    })).catch(error => {
      // Extract error message from the API response
      const message = error.body?.message || error.body?.error || error.message || 'An error occurred while updating the resource';
      
      // Check if it's a validation error for username field
      if (message.toLowerCase().includes('username') || 
          message.toLowerCase().includes('email') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('duplicate')) {
        // Create HttpError with validation errors for specific fields
        throw new HttpError(message, error.status || 422, {
          errors: {
            username: [message]
          }
        });
      }
      
      // For other errors, throw standard HttpError
      throw new HttpError(message, error.status || 500);
    });
  },
  
  delete: (resource, params) => {
    const teamId = localStorage.getItem('userTeamId');
    let url = '';
    
    switch (resource) {
      case 'contacts':
      case 'calendars':
      case 'settings':
        url = `teams/${teamId}/${resource}/${params.id}`;
        break;
      case 'users':
        url = `users/${params.id}`;
        break;
      case 'teams':
        url = `teams/${params.id}`;
        break;
      default:
        url = `${resource}/${params.id}`;
    }
    
    return httpClient(url, {
      method: 'DELETE',
    }).then(({ json }) => ({ data: { ...params.data, id: params.id } }));
  },
  
  // Implementations for standard methods that we might need
  getMany: (resource, params) => {
    const query = {
      ids: JSON.stringify(params.ids),
    };
    const queryString = fetchUtils.queryParameters(query);
    const url = `${resource}?${queryString}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },
  
  getManyReference: (resource, params) => {
    let url = '';
    
    if (resource === 'users' && params.target === 'team_id') {
      // Special case: getting users for a team
      url = `teams/${params.id}/users`;
    } else {
      // Standard endpoint with query params
      const query = {
        ...params.filter,
        [params.target]: params.id,
      };
      const queryString = fetchUtils.queryParameters(query);
      url = `${resource}?${queryString}`;
    }
    
    return httpClient(url).then(({ headers, json }) => {
      return {
        data: json,
        total: json.length,
      };
    });
  },
  
  updateMany: (resource, params) => {
    // Not implemented in API, could be added if needed
    return Promise.resolve({ data: [] });
  },
  
  deleteMany: (resource, params) => {
    // Not implemented in API, could be added if needed
    return Promise.resolve({ data: [] });
  }
};

// Add custom convenience methods for backward compatibility
const extendedDataProvider = {
  ...baseDataProvider,

  // Custom method to get all teams - for compatibility
  getTeams: () => {
    return baseDataProvider.getList('teams', {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'name', order: 'ASC' },
      filter: {}
    });
  }
};

export const dataProvider = extendedDataProvider;